import React, { useCallback, useState } from 'react'
import { compose } from 'recompose'
import { connect as connectStaticText } from '../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../StaticText/StaticTextContext'
import Users from '../../../Icon/icons/Users/index'
import UserFollow from '../../../Icon/icons/UserFollow/index'
import RemoveUser from '../../../Icon/icons/RemoveUser/index'
import CommunitySplitPillTag from '../CommunitySplitPillTag/index'
import type { CommunitySplitPillVariant } from '../CommunitySplitPillTag/CommunitySplitPillTag'

export interface FollowTagProps {
  isFollowing: boolean
  followerCount: number
  onClick?: () => void
  onUnfollow?: () => void
  disabled?: boolean
  staticText?: InternationalStrings
}

interface FollowTagContentProps {
  isFollowing: boolean
  followerCount: number
  onClick?: () => void
  onUnfollow?: () => void
  disabled?: boolean
}

function FollowTagContent (props: FollowTagContentProps) {
  const {
    isFollowing,
    onClick,
    onUnfollow,
    disabled = false,
  } = props

  const [isHovered, setIsHovered] = useState(false)

  const stFollowing = useTranslatedText({ keys: ['following'] })
  const stNotFollowing = useTranslatedText({ keys: ['notFollowing'] })
  const stFollow = useTranslatedText({ keys: ['follow'] })
  const stUnfollow = useTranslatedText({ keys: ['unfollow'] })

  const handleClick = useCallback(() => {
    if (disabled) return
    if (isFollowing && onUnfollow) {
      onUnfollow()
    } else if (!isFollowing && onClick) {
      onClick()
    }
  }, [isFollowing, onClick, onUnfollow, disabled])

  const handleMouseEnter = useCallback(() => setIsHovered(true), [])
  const handleMouseLeave = useCallback(() => setIsHovered(false), [])

  // Determine active icon, label, and variant based on isFollowing × isHovered
  type IconKey = 'users' | 'userFollow' | 'removeUser'
  let activeIcon: IconKey
  let currentLabel: string
  let variant: CommunitySplitPillVariant

  if (isFollowing && isHovered) {
    activeIcon = 'removeUser'
    currentLabel = stUnfollow
    variant = 'pink'
  } else if (isFollowing) {
    activeIcon = 'users'
    currentLabel = stFollowing
    variant = 'teal'
  } else if (isHovered) {
    activeIcon = 'userFollow'
    currentLabel = stFollow
    variant = 'teal'
  } else {
    activeIcon = 'users'
    currentLabel = stNotFollowing
    variant = 'gray'
  }

  // Icon crossfade: all three icons share a grid cell, only the active one
  // is visible. Opacity transition produces a smooth crossfade between states.
  const iconCrossfade = (
    <span className="follow-tag__icon-crossfade">
      <span className={activeIcon === 'users' ? 'follow-tag__icon-active' : undefined}>
        <Users size="16px" />
      </span>
      <span className={activeIcon === 'userFollow' ? 'follow-tag__icon-active' : undefined}>
        <UserFollow size="16px" />
      </span>
      <span className={activeIcon === 'removeUser' ? 'follow-tag__icon-active' : undefined}>
        <RemoveUser size="16px" />
      </span>
    </span>
  )

  // Size-stable label: ALL four labels share the same grid cell so the text
  // area is always the width of the longest label. Only the current label is
  // visible — the icon stays in a fixed position outside the grid.
  const stableLabel = (
    <span className="follow-tag__stable-label">
      <span aria-hidden={currentLabel !== stNotFollowing ? 'true' : undefined}>{stNotFollowing}</span>
      <span aria-hidden={currentLabel !== stFollow ? 'true' : undefined}>{stFollow}</span>
      <span aria-hidden={currentLabel !== stFollowing ? 'true' : undefined}>{stFollowing}</span>
      <span aria-hidden={currentLabel !== stUnfollow ? 'true' : undefined}>{stUnfollow}</span>
    </span>
  )

  return (
    <CommunitySplitPillTag
      leftIconSvg={iconCrossfade}
      leftLabel={stableLabel}
      // TODO: Re-enable for future release — follower count display
      // rightIconSvg={<User />}
      // rightLabel={followerCount}
      variant={variant}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disabled={disabled || (!onClick && !onUnfollow)}
      ariaLabel={currentLabel}
    />
  )
}

function FollowTag (props: FollowTagProps) {
  const { staticText, ...rest } = props
  return (
    <StaticTextProvider staticText={staticText}>
      <FollowTagContent {...rest} />
    </StaticTextProvider>
  )
}

export { FollowTag }

export default compose(
  connectStaticText({ storeKey: 'followTag' }),
)(React.memo(FollowTag))
